import * as React from "react";
import SectionRecord from "models/view-models/section-record";
import useCurrentPublication from "utilities/contexts/use-current-publication";
import useFeatureFlags from "utilities/hooks/use-feature-flags";
import { ClassNameConstants } from "constants/classname-constants";
import { SectionType } from "models/enumerations/section-type";
import { VirtualListRowProps } from "organisms/virtual-list/virtual-list";
import { XmlChangeNotationConstants } from "constants/xml-change-notation-constants";
import { forwardRef, Ref, useEffect, useMemo, useRef, useState } from "react";
import SectionDetailWithinPanel from "organisms/publication-content/notebook-reference-panel/section-detail-within-panel";
import useVirtualTableOfContents from "pages/notebooks/notebook-reference-panel/bookview/use-virtual-table-of-contents";
// -----------------------------------------------------------------------------------------
// #region Interfaces
// -----------------------------------------------------------------------------------------

interface SectionDetailWrapperWithinPanelProps
    extends VirtualListRowProps,
        React.PropsWithChildren {
    /**
     * Section to be displayed. **Note** If a part is meant to separate this section from another,
     * it should have the `part` navigation property populated by some means.
     * (For example, by `SectionCollectionRecord.getGroupedTopLevelSections`)
     */
    section: SectionRecord;

    /**
     * Flat list of Sections to display
     */

    /**
     * Is this SectionDetail being rendered in a VirtualList? (Applies the '-virtualized' className
     * if true)
     */
    virtualized?: boolean;

    onSectionHover: (id: number) => void;
}

// #endregion Interfaces

// -----------------------------------------------------------------------------------------
// #region Constants
// -----------------------------------------------------------------------------------------

// const SECTION_LEVEL_0_CLASS_NAME = "c-section-detail";
// const SECTION_LEVEL_1_CLASS_NAME = "c-sub-section-detail";
// const SECTION_LEVEL_2_CLASS_NAME = "c-sub-sub-section-detail";

const BASE_CSS_CLASSNAME = "c-section-wrapper";
const BREADCRUMB_MENU_HEIGHT = 41;
const SCROLLABLE_PARENT_CSS_CLASSNAME = "c-notebook-reference-panel-bookview";
export const SectionLevelClassNames = {
    zero: "c-section-detail",
    one: "c-sub-section-detail",
    two: "c-sub-sub-section-detail",
};

export const LevelModifiers = {
    zero: "-level-zero",
    one: "-level-one",
    two: "-level-two",
};

// #endregion Constants

// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

const SectionDetailWrapperWithinPanel = forwardRef<
    HTMLElement,
    SectionDetailWrapperWithinPanelProps
>((props: SectionDetailWrapperWithinPanelProps, ref: Ref<HTMLElement>) => {
    const { section, children, onSectionHover } = props;

    const sectionRef = useRef(null);

    const { useReducedRendersInBookview } = useFeatureFlags();

    const [showContents, setShowContents] = useState(
        !useReducedRendersInBookview
    );

    const { currentTocItem } = useVirtualTableOfContents();

    const { publication } = useCurrentPublication();

    // Observes entire section, and shows its contents if on screen
    const sectionObserver = useReducedRendersInBookview
        ? new IntersectionObserver(
              (entries) => {
                  entries.forEach((entry) => {
                      if (entry.isIntersecting && !showContents) {
                          setShowContents(true);
                          sectionObserver?.disconnect();
                      }
                  });
              },
              {
                  root: null,
                  threshold: 0,
              }
          )
        : null;

    // Attaches observer once section is available
    useEffect(() => {
        if (sectionRef.current && useReducedRendersInBookview) {
            sectionObserver?.observe(sectionRef.current!);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sectionRef]);

    useEffect(() => {
        if (currentTocItem?.id === section.id) {
            const sectionElement = document.getElementById(
                section?.id?.toString()!
            );
            const scrollableElement = document.getElementsByClassName(
                SCROLLABLE_PARENT_CSS_CLASSNAME
            )[0];
            const sectionTop =
                sectionElement?.getBoundingClientRect().top! -
                BREADCRUMB_MENU_HEIGHT;
            scrollableElement?.scrollTo({
                top: sectionTop,
            });
        }
    }, [currentTocItem?.id, currentTocItem?.parentId, section.id]);

    const { cssBaseClass, levelModifier } = useMemo(() => {
        switch (section.type) {
            case SectionType.Section:
                return {
                    cssBaseClass: SectionLevelClassNames.zero,
                    levelModifier: LevelModifiers.zero,
                };
            case SectionType.SubSection:
                return {
                    cssBaseClass: SectionLevelClassNames.one,
                    levelModifier: LevelModifiers.one,
                };
            case SectionType.SubSubSection:
            default:
                return {
                    cssBaseClass: SectionLevelClassNames.two,
                    levelModifier: LevelModifiers.two,
                };
        }
    }, [section.type]);
    const classNames = [cssBaseClass];

    const titleClassName: string = `${cssBaseClass}__title ${section.getChangesClassModifier()}`;

    const sectionLabel = useMemo(
        () =>
            getLabel({
                sectionLabel: section.getLabel(),
                sectionTitleHasLabel: publication?.sectionTitleHasLabel ?? true,
                includeSectionLabel:
                    publication?.includeSectionLabel() ?? false,
            }),
        [publication, section]
    );

    const deleted = section.changes === XmlChangeNotationConstants.DELETION;
    const deletedClassName = deleted ? ClassNameConstants.Deleted : "";

    if (deleted) {
        classNames.push(deletedClassName);
    }

    return (
        <div
            className={`${BASE_CSS_CLASSNAME}__fragment ${
                showContents ? "" : "-hidden"
            }`}
            ref={sectionRef}>
            <section
                id={section.id?.toString()}
                data-root-section-id={section.rootSectionId}
                className={`${BASE_CSS_CLASSNAME} ${deletedClassName} ${levelModifier}`}
                data-test-display-sequence={section.displaySequence}
                data-test-part-id={section.partId}
                ref={ref}>
                <SectionDetailWithinPanel
                    onSectionHover={onSectionHover}
                    classNames={classNames}
                    cssBaseClass={cssBaseClass}
                    renderContent={showContents}
                    section={section}
                    sectionLabel={sectionLabel}
                    titleClassName={titleClassName}
                />
                {children}
            </section>
        </div>
    );
});

// #endregion Component

// -----------------------------------------------------------------------------------------
// #region Export
// -----------------------------------------------------------------------------------------
export default SectionDetailWrapperWithinPanel;

// #endregion Export

// -----------------------------------------------------------------------------------------
// #region Private Functions
// -----------------------------------------------------------------------------------------

interface GetLabelProps {
    sectionLabel: string;
    sectionTitleHasLabel: boolean;
    includeSectionLabel: boolean;
}

const getLabel = ({ sectionLabel, sectionTitleHasLabel }: GetLabelProps) => {
    if (sectionTitleHasLabel) {
        return;
    }

    return <React.Fragment>{`${sectionLabel} `}</React.Fragment>;
};

// #endregion Private Functions
